import { Pin } from '../types';

/**
 * Parses a user-entered pin specification into ordered labels. Commas,
 * whitespace and newlines all act as separators; blank entries are dropped and
 * duplicate labels removed (first occurrence wins).
 */
export function parsePinLabels(input: string): string[] {
  const labels = input
    .split(/[\s,]+/)
    .map(label => label.trim())
    .filter(label => label.length > 0);
  return Array.from(new Set(labels));
}

/**
 * Builds pin instances from labels, reusing the id of any existing pin whose
 * label is unchanged so wire-to-pin assignments survive an edit.
 */
export function buildPins(labels: string[], existing: Pin[], generateId: () => string): Pin[] {
  const byLabel = new Map(existing.map(pin => [pin.label, pin]));
  return labels.map(label => byLabel.get(label) ?? { id: generateId(), label });
}

/**
 * Picks a label for a pin added on the fly: the smallest positive integer not
 * already used on the connector, as a string. Keeps auto-added pins collision
 * free so a later {@link buildPins} edit never merges them by accident.
 */
export function nextPinLabel(existing: Pin[]): string {
  const used = new Set(existing.map(pin => pin.label));
  let candidate = 1;
  while (used.has(String(candidate))) {
    candidate++;
  }
  return String(candidate);
}
